/**
 * Byråets stamdata om artisten — bandet, ridere og promo (vedtak 96.1, migrering 0091).
 *
 * ---------------------------------------------------------------------------------------------
 * HVEM EIER SANNHETEN. Atle, 8. september: «Byrået eier stamdata, festivalen arver en KOPI ved
 * booking.» Det samme finnes på festivalens side som ADVANCE på én programpost — allergiene i
 * hospitality-seksjonen, rideren som en fil på den ene bookingen — og det er riktig der. Her er
 * det data om bandet som gjelder alle gigs: fylt ut én gang, sendt med til hver arrangør.
 *
 * OVERFØRINGEN ER IKKE BYGGET ENNÅ, og kontrakten later ikke som. Det som finnes er registeret;
 * kopien inn i en bekreftet booking er neste steg, og den er en skriving hos festivalen — ikke et
 * felt her.
 *
 * ---------------------------------------------------------------------------------------------
 * ET MEDLEM ER IKKE EN `person`. `persons` er menneskene som kan logge inn og finnes én gang på
 * tvers av hus; en trommeslager skal ikke bli en identitet i plattformen fordi noen førte opp
 * allergien deres. Derfor bærer denne DTO-en et NAVN og ikke en `personId`.
 */
import { z } from 'zod';
import { bandShareAccessSchema } from './artist-share.js';
import {
  artistAccessAddedBySchema,
  artistAccessSchema,
  artistAccessStatusSchema,
  artistRosterAccessSchema,
} from './artist-access.js';
import { engagementRefDtoSchema } from './engagements.js';

import { performanceStatusSchema } from '../enums.js';
import { urlInputSchema } from '../url.js';
import { personStandardFieldsPatchSchema } from './person-card.js';

/**
 * Hva en lenke ER, og hvorfor dette er en lukket liste når kontakttypene ikke er det.
 *
 * `kind` bestemmer hva som skjer med raden ved en bekreftet booking (vedtak 96.1): en teknisk
 * rider går inn i festivalens advance, en promolenke gjør ikke det. En verdi organisasjonen selv
 * fant på ville ikke hatt noe sted å gå — mens en kontakttype bare skal leses av mennesker.
 */
export const ARTIST_LINK_KINDS = [
  'tech_rider',
  'hospitality_rider',
  'stage_plot',
  /*
   * INPUTLISTE OG FORSIKRING (QA A7).
   *
   * De to er det arrangøren spør etter rett etter rideren, og de manglet i lista — så de havnet
   * under «Annet», der de ikke kan sendes noe sted automatisk. En inputliste hører til Teknisk
   * som rideren gjør; et forsikringsbevis er et papir arrangøren må ha, ikke en fil om musikken.
   */
  'input_list',
  'insurance',
  'promo',
  'press_photo',
  'music',
  'video',
  'other',
] as const;
export type ArtistLinkKind = (typeof ARTIST_LINK_KINDS)[number];
export const artistLinkKindSchema = z.enum(ARTIST_LINK_KINDS).meta({ id: 'ArtistLinkKind' });

/**
 * ORDENE ET MENNESKE FAKTISK SKRIVER, OVERSATT TIL NØKKELEN (QA runde 6, punkt 10 og P0.1).
 *
 * ================================================================================================
 * Siden 0113 er `kind` FRITEKST, og det er riktig: et byrå skal kunne skrive «Plateselskap».
 * Men de ti ordene produktet selv sender med betyr noe — de avgjør hvilken advance-seksjon fila
 * havner i, og hvilke fire dokumenter sjekklista på artistoversikten leter etter.
 *
 * Rapporten fant begge sidene av det på én gang. Atle skrev «Stage plot» og «Pressebilde» i
 * stedet for å plukke fra lista, og da:
 *
 *   · havnet pressebildet under Teknisk framfor Promo, fordi `SECTION_FOR_KIND` ikke kjente ordet
 *   · sa artistoversikten «Stage plot: Ingenting lastet opp» om et dokument som lå der
 *
 * ================================================================================================
 * DEN KJENNER TRE SKRIVEMÅTER, OG BARE DE TRE.
 *
 * Nøkkelen selv (`stage_plot`), nøkkelen med mellomrom («stage plot»), og bransjeordet på norsk
 * («scenetegning»). Alt annet er fritekst og kommer ut som det kom inn — det er hele poenget med
 * at kolonnen er fri.
 *
 * TERMS.md sier at stage plot, rider, promo og press photo BLIR STÅENDE på engelsk i det norske
 * produktet. Da må de også kunne skrives på engelsk uten at produktet later som det ikke forstår.
 */
const KIND_ALIASES: Readonly<Record<string, ArtistLinkKind>> = {
  'teknisk rider': 'tech_rider',
  'technical rider': 'tech_rider',
  rider: 'tech_rider',
  'hospitality rider': 'hospitality_rider',
  scenetegning: 'stage_plot',
  scenekart: 'stage_plot',
  inputliste: 'input_list',
  'input list': 'input_list',
  forsikringsbevis: 'insurance',
  forsikring: 'insurance',
  pressebilde: 'press_photo',
  pressebilder: 'press_photo',
  'press photo': 'press_photo',
  promo: 'promo',
  musikk: 'music',
  video: 'video',
  annet: 'other',
};

/** Nøkkelen når ordet er en av de ti, ellers ordet slik det ble skrevet. */
export function artistLinkKindOf(written: string): string {
  const trimmed = written.trim();
  if (trimmed === '') return trimmed;

  /* Mellomrom, bindestrek og understrek er det samme skillet for et menneske som skriver. */
  const flat = trimmed.toLowerCase().replace(/[\s_-]+/g, ' ');
  const asKey = flat.replace(/ /g, '_');
  if ((ARTIST_LINK_KINDS as readonly string[]).includes(asKey)) return asKey;

  return KIND_ALIASES[flat] ?? trimmed;
}

const nameSchema = z.string().trim().min(1).max(200);
const shortTextSchema = z.string().trim().max(200);
const longTextSchema = z.string().trim().max(2000);

export const bookingArtistMemberDtoSchema = z
  .object({
    id: z.uuid(),
    /**
     * KONTAKTEN RADEN PEKER PÅ (0282, QA runde 12 #36).
     *
     * «Personen kobles, ikke kopieres. Navn, telefon og e-post leses fra personen. Rolle,
     * instrument og "spiller i"-formater lagres på koblingen.»
     *
     * NULL ER LOVLIG OG IKKE EN MANGEL. Radene fra før 0282 bærer navnet sitt selv, og skjemaet
     * kan fortsatt skrive et navn uten å opprette en kontakt — se `create`-forespørselen. Når den
     * er satt, er `displayName`, `email` og `phone` under LEST fra kontakten, ikke fra raden.
     */
    contactId: z.uuid().nullable(),
    /**
     * Mennesket bak kontakten, for «Send melding» på medlemskortet (sak 216.9). Null uten kontakt.
     * Valgfri så eldre klienter og faste testdata tåler svaret.
     */
    personId: z.uuid().nullable().optional(),
    displayName: z.string(),
    role: z.string().nullable(),
    instrument: z.string().nullable(),
    /**
     * Radens gamle kopier — alltid null siden grep A1 (0915). Kostholdet står på personen og kommer
     * fra kortet under, for alle i byrået (1077).
     */
    allergies: z.string().nullable(),
    dietary: z.string().nullable(),
    /** «Allergier og kosthold» fra personen, som tekst (A4). */
    food: z.string().nullable().optional(),
    /** Kostholdet er lest fra kortet. Alltid for byråets egne folk (1077); aldri for en gjest. */
    healthVisible: z.boolean().optional(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    /** Hvor personen reiser fra (0114). Detaljpanelet i §5.6 viser den; ingen sortering på den. */
    basedIn: z.string().nullable(),
    notes: z.string().nullable(),
    /** Om personen faktisk reiser: en produsent som blir hjemme teller ikke i reise og hotell. */
    travels: z.boolean(),
    /** Formatene personen er merket med (§5.2). Tom liste = med i ingen, som er en ekte tilstand. */
    formatIds: z.array(z.uuid()),
    /** Innloggingen byrået har til mennesket (sak 216): ingen, invitert, forespurt eller aktiv. */
    login: z.enum(['none', 'invited', 'requested', 'active']),
    /* 1080 (§2.1): tilgangen som kolonne, og «Lagt til av» når det ikke var byrået. */
    access: artistAccessSchema.nullable().optional(),
    accessStatus: artistAccessStatusSchema.optional(),
    grantId: z.uuid().nullable().optional(),
    addedBy: artistAccessAddedBySchema.nullable().optional(),
  })
  .meta({ id: 'BookingArtistMember' });

/**
 * EN OPPFØRING UNDER «DOCUMENTS AND FILES» (ARTISTS.md §5.6, migrering 0113).
 *
 * ---------------------------------------------------------------------------------------------
 * Atle, 10. september: «Every entry accepts an upload, a link, or both.»
 *
 * DET VAR TO KORT FORDI DET VAR TO TABELLER: lenker fra 0091, filer som vedlegg fra 0105. Å slå
 * kortene sammen uten å slå sammen radene ville vært en løgn — «en lenke, en fil, eller begge» kan
 * ikke stemme når raden ER enten det ene eller det andre. En teknisk rider som finnes både som PDF
 * og som en delt mappe er ÉN rider.
 *
 * `kind` ER FRITEKST siden 0113, og de åtte gamle ordene lever videre som data. Skjermen oversetter
 * dem den kjenner igjen; et ord huset finner på står uoversatt, som er det riktige svaret på et ord
 * vi ikke har.
 *
 * MINST ÉN AV `url` OG `attachment`. Basen håndhever det: en tittel som peker på ingenting er
 * ikke en oppføring.
 */
export const bookingArtistLinkDtoSchema = z
  .object({
    id: z.uuid(),
    kind: z.string(),
    label: z.string().nullable(),
    url: z.string().nullable(),
    /** Fila, når oppføringen har en. Navn og størrelse leses gjennom pekeren, ikke kopiert hit. */
    attachment: z
      .object({
        id: z.uuid(),
        fileName: z.string(),
        byteSize: z.number().int(),
        /** Usann mens viruskontrollen venter, eller om fila er smittet — samme port som ellers. */
        downloadable: z.boolean(),
      })
      .nullable(),
    /**
     * AVTALEDOKUMENTET (0280, QA runde 12 #33).
     *
     * «Avtaledokumenter ligger IKKE i Stamdata › Dokumenter og filer. De kan ALDRI velges i
     * transfer-popupen eller deles med arrangør.»
     *
     * Feltet står på DTO-en selv om de to listene er delt i `links` og `agreementDocuments`, fordi
     * den samme raden kan komme fra begge og skjermen skal kunne tegne låsen uten å vite hvilken
     * liste den kom fra. Håndhevingen står i basen (0281), ikke her.
     */
    isAgreement: z.boolean(),
  })
  .meta({ id: 'BookingArtistLink' });

/**
 * EN STØRRELSE ARTISTEN KAN BOOKES I (ARTISTS.md §5.2).
 *
 * ---------------------------------------------------------------------------------------------
 * Atle, 10. september: «The format changes the fee, the travelling party and the rider, so it
 * cannot be a note in a text field.»
 *
 * `isDefault` ER SVARET PÅ TO SPØRSMÅL, og derfor er det ett felt og ikke to: hvilken pris
 * artistlista viser, og hvilket format en forespørsel uten et valg får. Basen håndhever at det
 * finnes nøyaktig ett.
 *
 * `travellingParty` er NULL når ingen har sagt det, og 0 er et ekte svar — en DJ som møter på
 * stedet reiser ikke. De to er forskjellige, og et tall som foldet dem sammen ville sagt «ingen
 * kommer» om en artist ingen har talt.
 */
export const bookingArtistFormatDtoSchema = z
  .object({
    id: z.uuid(),
    name: z.string(),
    travellingParty: z.number().int().nullable(),
    askingFeeMinor: z.number().int().nullable(),
    /** Spillelengden i denne størrelsen (§5.2, QA-punkt 7). Null: ingen har sagt den. */
    setLengthMinutes: z.number().int().positive().nullable(),
    currency: z.string().length(3),
    description: z.string().nullable(),
    isDefault: z.boolean(),
    sortOrder: z.number().int(),
    /** Hvor mange i reisefølget som er merket med dette formatet. Roster-fanen teller dem. */
    memberCount: z.number().int(),

    /**
     * ANNONSEN FOR NETTOPP DETTE FORMATET (0120, ARTISTS.md §5.7).
     *
     * «One row per format, listed or not listed independently.» En artist som er ute som TRIO til
     * 140 000 er ikke ute som FULL BAND til 250 000, og en annonse per artist solgte feil vare til
     * den som bare hadde plass til tre.
     *
     * `marketplaceViews` er et TALL og ikke en logg: hvem som så en annonse er ikke noe produktet
     * skal kunne lese ut, og spørsmålet som faktisk stilles er «er den sett».
     */
    marketplacePitch: z.string().nullable(),
    marketplacePublishedAt: z.iso.datetime({ offset: true }).nullable(),
    marketplaceViews: z.number().int().nonnegative(),
  })
  .meta({ id: 'BookingArtistFormat' });
export type BookingArtistFormatDto = z.infer<typeof bookingArtistFormatDtoSchema>;

const formatName = z.string().trim().min(1).max(60);
const partySize = z.number().int().min(0).max(200);
const formatFee = z.number().int().min(0).max(100_000_000_00);

export const createBookingArtistFormatRequestSchema = z
  .object({
    name: formatName,
    travellingParty: partySize.nullish(),
    askingFeeMinor: formatFee.nullish(),
    /** Spillelengden i denne størrelsen (§5.2, QA-punkt 7, migrering 0162). */
    setLengthMinutes: z.number().int().positive().max(1440).nullish(),
    description: z.string().trim().max(2000).nullish(),
    /**
     * Å be om standardmerket FLYTTER det, som honorarkategorien i vedtak 85: den som hadde det
     * mister det i samme skriving. Alternativet — en feilmelding om at et annet format er
     * standard — ville tvunget den som bytter til å gjøre to ting i riktig rekkefølge for én.
     */
    isDefault: z.boolean().optional(),
  })
  .meta({ id: 'CreateBookingArtistFormatRequest' });
export type CreateBookingArtistFormatRequest = z.infer<
  typeof createBookingArtistFormatRequestSchema
>;

/** Å legge ETT format ut, eller ta det ned. Teksten er per annonse (§5.7). */
export const listFormatRequestSchema = z
  .object({
    published: z.boolean(),
    pitch: z.string().trim().max(2000).nullish(),
  })
  .meta({ id: 'ListFormatRequest' });

export type ListFormatRequest = z.infer<typeof listFormatRequestSchema>;

export const updateBookingArtistFormatRequestSchema = z
  .object({
    name: formatName.optional(),
    travellingParty: partySize.nullish(),
    askingFeeMinor: formatFee.nullish(),
    /** Spillelengden i denne størrelsen (§5.2, QA-punkt 7, migrering 0162). */
    setLengthMinutes: z.number().int().positive().max(1440).nullish(),
    description: z.string().trim().max(2000).nullish(),
    isDefault: z.boolean().optional(),
    sortOrder: z.number().int().min(0).max(9999).optional(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: 'At least one field must be given.',
  })
  .meta({ id: 'UpdateBookingArtistFormatRequest' });
export type UpdateBookingArtistFormatRequest = z.infer<
  typeof updateBookingArtistFormatRequestSchema
>;

/**
 * HVILKE FORMATER ETT MENNESKE ER MED I — satt som et HELT SETT, ikke ett merke om gangen.
 *
 * En rad i reisefølget svarer på «hvem spiller i hva», og det svaret redigeres som en gruppe
 * avkryssinger. Ett endepunkt per merke ville betydd tre kall for å flytte en bassist fra bandet
 * til trioen, og en halvveis tilstand imellom hvis det andre feilet.
 */
export const setMemberFormatsRequestSchema = z
  .object({ formatIds: z.array(z.uuid()).max(50) })
  .meta({ id: 'SetMemberFormatsRequest' });
export type SetMemberFormatsRequest = z.infer<typeof setMemberFormatsRequestSchema>;
/**
 * ETT MENNESKE RUNDT ARTISTEN — manager, tour manager, agent (migrering 0106).
 *
 * ---------------------------------------------------------------------------------------------
 * NAVNET, E-POSTEN OG TELEFONEN ER KONTAKTREGISTERETS, ikke denne radens: raden er en PEKER, og
 * feltene under er lest gjennom den. En kopi her ville vært utdatert dagen etter noen rettet
 * nummeret ett sted — som er hele grunnen til at et byrå slutter å stole på en kontaktliste.
 *
 * `role` er FRITEKST. «manager», «tour manager», «agent», «booker», «label» er bransjens ord og
 * ikke våre, og huset som trenger et syttende skal ikke måtte legge det under «Annet» (prinsipp 9).
 * Skjemaet foreslår de vanlige; det er DET som gjør lista tellbar.
 */
export const bookingArtistContactDtoSchema = z
  .object({
    id: z.uuid(),
    contactId: z.uuid(),
    /** D4: personen bak kontakten — mottakeren når artisten deles med en manager. */
    personId: z.uuid().nullable().optional(),
    /** Fra kontaktregisteret, lest gjennom pekeren. */
    displayName: z.string(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    companyName: z.string().nullable(),
    role: z.string().nullable(),
    sortOrder: z.number().int(),
    /* 1080 (§2.1): tilgangen som kolonne, og «Lagt til av» når det ikke var byrået. */
    access: artistAccessSchema.nullable().optional(),
    accessStatus: artistAccessStatusSchema.optional(),
    grantId: z.uuid().nullable().optional(),
    addedBy: artistAccessAddedBySchema.nullable().optional(),
  })
  .meta({ id: 'BookingArtistContact' });
export type BookingArtistContactDto = z.infer<typeof bookingArtistContactDtoSchema>;

/**
 * ET MENNESKE PÅ ARTISTEN — ENTEN EN SOM FINNES, ELLER ET NYTT NAVN (QA-punkt 22).
 *
 * ---------------------------------------------------------------------------------------------
 * Rapporten: «Management kan bare velges fra eksisterende kontakter og ikke opprettes der man
 * står. Man må gå en omvei via Kontakter.»
 *
 * Omveien er dyrere enn den ser ut: man forlater artisten man holder på med, oppretter noen i et
 * register med sine egne felter, og må så finne tilbake. Den som legger inn en agent midt i en
 * telefonsamtale gjør det ikke — de skriver navnet i et notat i stedet.
 *
 * ENTEN-ELLER, ALDRI BEGGE. `contactId` peker på en som finnes; `displayName` lager en ny. To
 * felter der begge kunne vært satt ville reist spørsmålet om hvilket som vinner, og svaret ville
 * bodd i koden framfor i kontrakten.
 */
export const createBookingArtistContactRequestSchema = z
  .object({
    contactId: z.uuid().nullish(),
    /** Navnet på en som ikke står i registeret ennå. Lager kontakten og koblingen i ett kall. */
    displayName: z.string().trim().min(1).max(120).nullish(),
    /**
     * TELEFON OG E-POST PÅ DEN NYE (QA runde 9, P2 #7).
     *
     * Raden VISTE dem allerede — «{{telefon}} · {{e-post}}» under navnet — mens skjemaet ikke
     * kunne sette dem. Så en agent man la inn her sto for alltid uten nummeret man nettopp hadde
     * ringt, og omveien via Kontakter var tilbake for akkurat de to feltene denne veien ble bygget
     * for å slippe.
     *
     * Bare ved OPPRETTELSE. Peker raden på en kontakt som finnes, er det Kontakter som eier
     * opplysningene — vedtak 117: «registeret er menneskene organisasjonen har et forhold til».
     * Å kunne rette en telefon to steder ville vært to sannheter om det samme mennesket.
     */
    email: z.email().max(320).nullish(),
    phone: shortTextSchema.nullish(),
    role: shortTextSchema.nullish(),
    sortOrder: z.number().int().min(0).max(9999).optional(),
    /**
     * A20 / 267.2 a: en ny person blir ikke kontakt av å stå i management. «Legg i kontakter» er av
     * som standard; med `true` legges personen (ny eller valgt) i Kontakter, delt med organisasjonen.
     */
    addToContacts: z.boolean().optional(),
    /** §2.2 (1080): innlogging eller «Bare oppført». Uten feltet: bare oppført. */
    access: artistRosterAccessSchema.optional(),
  })
  .refine((value) => (value.contactId == null) !== (value.displayName == null), {
    message: 'Give either contactId or displayName.',
    path: ['contactId'],
  })
  .meta({ id: 'CreateBookingArtistContactRequest' });
export type CreateBookingArtistContactRequest = z.infer<
  typeof createBookingArtistContactRequestSchema
>;

/** Å rette rollen eller rekkefølgen. Mennesket byttes ikke ut — da er det en annen rad. */
export const updateBookingArtistContactRequestSchema = z
  .object({
    role: shortTextSchema.nullish(),
    sortOrder: z.number().int().min(0).max(9999).optional(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: 'At least one field must be given.',
  })
  .meta({ id: 'UpdateBookingArtistContactRequest' });
export type UpdateBookingArtistContactRequest = z.infer<
  typeof updateBookingArtistContactRequestSchema
>;

/**
 * Hele stamdatabildet for én artist, i ett svar.
 *
 * Ikke paget, og ikke tre kall: et band har medlemmer i tosifret antall og en håndfull lenker, og
 * siden viser alt sammen samtidig. Tre round trips for å tegne ett kort ville vært tre ting som
 * kan komme ut av takt på skjermen.
 */
/**
 * EN UTILGJENGELIG DAG ELLER ET NOTAT (0116, ARTISTS.md §5.4).
 *
 * ---------------------------------------------------------------------------------------------
 * Atle, 10. september: kalenderen tar «unavailable days (holidays, other commitments) and plain
 * notes (birthdays, anything worth seeing)».
 *
 * TO SLAG I ÉN LISTE, fordi forskjellen er ÉN ting: om dagen er stengt. Alt annet ved dem er likt,
 * og en skjerm som skal tegne måneden vil ha dem i samme svar uansett.
 *
 * ET INTERVALL, ikke en dag: en ferie er to uker. `endsOn` er lik `startsOn` når det gjelder én dag,
 * så leseren slipper å gjette hva null betyr.
 */
export const ARTIST_CALENDAR_KINDS = ['unavailable', 'note'] as const;
export type ArtistCalendarKind = (typeof ARTIST_CALENDAR_KINDS)[number];
export const artistCalendarKindSchema = z
  .enum(ARTIST_CALENDAR_KINDS)
  .meta({ id: 'ArtistCalendarKind' });

export const bookingArtistCalendarEntryDtoSchema = z
  .object({
    id: z.uuid(),
    kind: artistCalendarKindSchema,
    startsOn: z.iso.date(),
    endsOn: z.iso.date(),
    title: z.string(),
    note: z.string().nullable(),
  })
  .meta({ id: 'BookingArtistCalendarEntry' });

export type BookingArtistCalendarEntryDto = z.infer<typeof bookingArtistCalendarEntryDtoSchema>;

const calendarTitle = z.string().trim().min(1).max(200);

export const createBookingArtistCalendarEntryRequestSchema = z
  .object({
    kind: artistCalendarKindSchema,
    startsOn: z.iso.date(),
    endsOn: z.iso.date(),
    title: calendarTitle,
    note: longTextSchema.nullish(),
  })
  .refine((value) => value.endsOn >= value.startsOn, {
    path: ['endsOn'],
    /* Datoer som TEKST: ISO-datoer sorterer leksikografisk, og en tidssone kan ikke forstyrre det. */
    message: 'The last day cannot come before the first.',
  })
  .meta({ id: 'CreateBookingArtistCalendarEntryRequest' });

export const updateBookingArtistCalendarEntryRequestSchema = z
  .object({
    kind: artistCalendarKindSchema.optional(),
    startsOn: z.iso.date().optional(),
    endsOn: z.iso.date().optional(),
    title: calendarTitle.optional(),
    note: longTextSchema.nullish(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: 'At least one field must be given.',
  })
  .meta({ id: 'UpdateBookingArtistCalendarEntryRequest' });

export type CreateBookingArtistCalendarEntryRequest = z.infer<
  typeof createBookingArtistCalendarEntryRequestSchema
>;
export type UpdateBookingArtistCalendarEntryRequest = z.infer<
  typeof updateBookingArtistCalendarEntryRequestSchema
>;

export const bookingArtistRosterDtoSchema = z
  .object({
    artistId: z.uuid(),
    artistName: z.string(),
    homeTown: z.string().nullable(),
    genre: z.string().nullable(),
    /**
     * «Band», «Soloartist», «DJ» — og grunnen til at den kom til i runde 9 (P2 #12).
     *
     * Rapporten: TYPE er tom for en artist tatt inn fra markedsplassen. Målingen viste at kopien
     * virker — `adopt` bærer feltet, og porten returnerer det — mens KILDEN er tom på hver eneste
     * av byråets artister. Den var tom fordi byrået ikke hadde noe sted å fylle den ut: skjemaet
     * hadde navn, sjanger, sted og pris, og arrangørens kolonne viste et felt ingen kunne sette.
     */
    artistType: z.string().nullable(),
    /**
     * HVEM HOS OSS SOM SVARER FOR DENNE ARTISTEN (sak 118, QA runde 10).
     *
     * Atle: «Et byrå setter en ansvarlig per ARTIST ... Den ansvarlige skal vises på artisten i
     * bookingsiden, på samme måte som i Greenroom.» Kolonnen har ligget i basen siden 0184 og
     * varselporten har lest den hele tiden; det som manglet var å VISE den der den settes.
     */
    assigneePersonId: z.uuid().nullable(),
    assigneeName: z.string().nullable(),
    /* Markedsplassen (0097): byråets eget tall, teksten de skrev, og om annonsen står ute. */
    askingFeeMinor: z.number().int().nullable(),
    currency: z.string().length(3),
    marketplacePitch: z.string().nullable(),
    marketplacePublishedAt: z.iso.datetime({ offset: true }).nullable(),
    /*
     * MENNESKENE RUNDT ARTISTEN (migrering 0106), i rekkefølge. Den FØRSTE er den man ringer.
     *
     * 0102 hadde fire flate felter her — én kontakt, ett navn, én e-post, ett nummer — og det var
     * for kort: rundt en artist står det en manager, en tour manager og en agent. Lista erstattet
     * dem samme døgn, og det ene svaret på «hvem ringer jeg» er nå rekkefølgen.
     */
    contacts: z.array(bookingArtistContactDtoSchema),
    /** Størrelsene artisten kan bookes i (§5.2). Alltid minst én: basen fikk den i 0111. */
    formats: z.array(bookingArtistFormatDtoSchema),
    members: z.array(bookingArtistMemberDtoSchema),
    /**
     * Stamdata › Dokumenter og filer. AVTALEDOKUMENTENE ER IKKE HER (#33) — de står i
     * `agreementDocuments` under, og transfer-popupen leser NETTOPP denne lista. Å dele dem i to
     * på serveren er det som gjør at popupen ikke kan tilby avtalen i det hele tatt.
     */
    links: z.array(bookingArtistLinkDtoSchema),
    /**
     * Avtale-fanens «Dokument og særvilkår» — den signerte avtalen, i flere versjoner eller med
     * tillegg (#33). Deles ALDRI med arrangøren; se 0281 for hvor det håndheves.
     */
    agreementDocuments: z.array(bookingArtistLinkDtoSchema),
    /** Utilgjengelige dager og notater (§5.4). Følger rosteren: kalenderen tegner dem sammen. */
    calendar: z.array(bookingArtistCalendarEntryDtoSchema),
    /** «Hele bandet» (276.11 c, 1059): det innloggingene i bandet arver. */
    bandAccess: bandShareAccessSchema.default({
      scopes: ['calendar', 'bookings', 'documents', 'advance', 'chat'],
      permission: 'view',
    }),
    /**
     * INVITASJONSLENKEN, ÉN GANG (persontypene §2.2, 1080; G «Til B» 4): bare i svaret på å legge til en
     * person med innlogging som ikke har konto ennå. E-posten går som før; lenken vises én gang, som
     * «Gi innlogging» gjør. Aldri lagret i klartekst og aldri i `GET`.
     */
    invitationToken: z.string().optional(),
  })
  .meta({ id: 'BookingArtistRoster' });

/**
 * ET MENNESKE I BANDET — ENTEN EN KONTAKT SOM FINNES, ELLER ET NYTT NAVN (QA runde 12, #36).
 *
 * ---------------------------------------------------------------------------------------------
 * Samme enten-eller som management fikk i QA-punkt 22, og av samme grunn: «"Legg til" … åpner søk
 * og valg blant org-ens kontakter … med "+ Ny person" nederst», og «En ny person som opprettes
 * her, blir også en kontakt i org-en og kan gjenbrukes på andre artister».
 *
 * `contactId` PEKER, `displayName` OPPRETTER. Begge satt ville reist spørsmålet om hvilket som
 * vinner, og svaret ville bodd i koden framfor i kontrakten.
 *
 * ALLERGIER OG KOSTBEHOV STÅR PÅ PERSONEN (grep A1, 0915; erstatter #36c). De skrives i byråets
 * opplysninger om personen (`person_facts`), aldri på raden og aldri i kontakten.
 */
const bookingArtistMemberFields = z.object({
  contactId: z.uuid().nullish(),
  displayName: nameSchema.nullish(),
  role: shortTextSchema.nullish(),
  instrument: shortTextSchema.nullish(),
  /** Gammel form: skrives som byråets matnotat på personen (`person_facts`), aldri på raden. */
  allergies: longTextSchema.nullish(),
  dietary: longTextSchema.nullish(),
  /* Grep A1 og A4: kostholdet på personen, som tekst. */
  food: personStandardFieldsPatchSchema.shape.food,
  phone: z.string().trim().max(40).nullish(),
  email: z.email().max(320).nullish(),
  basedIn: shortTextSchema.nullish(),
  notes: longTextSchema.nullish(),
  travels: z.boolean().optional(),
});

export const createBookingArtistMemberRequestSchema = bookingArtistMemberFields
  .extend({
    /**
     * 267.2, UNNTAKET: musikere hos byrå. Skjemaet har «Legg i kontakter» PÅ som standard her, og bare
     * her. API-et lager ikke kontakt uten at feltet er `true` (A20).
     */
    addToContacts: z.boolean().optional(),
    /** §2.2 (1080): innlogging eller «Bare oppført». Uten feltet: bare oppført. */
    access: artistRosterAccessSchema.optional(),
  })
  .refine((value) => (value.contactId == null) !== (value.displayName == null), {
    message: 'Give either contactId or displayName.',
    path: ['contactId'],
  })
  .meta({ id: 'CreateBookingArtistMemberRequest' });

/**
 * Å RETTE RADEN. Mennesket byttes IKKE ut — da er det en annen rad, som hos management.
 *
 * Derfor er `contactId` utelatt her og ikke bare valgfri: en oppdatering som kunne flytte
 * koblingen ville gjort «hvem er dette» til noe man kan endre uten at rollen, instrumentet og
 * formatmerkene følger med, og de er nettopp det som gjør raden til en KOBLING.
 */
export const updateBookingArtistMemberRequestSchema = bookingArtistMemberFields
  .omit({ contactId: true })
  .partial()
  .refine((value) => Object.keys(value).length > 0, {
    message: 'At least one field must be given.',
  })
  .meta({ id: 'UpdateBookingArtistMemberRequest' });

export const createBookingArtistLinkRequestSchema = z
  .object({
    /** Fritekst med forslag i skjemaet (§5.6). Se DTO-en for hvorfor lista ikke er lukket. */
    kind: z.string().trim().min(1).max(60),
    label: shortTextSchema.nullish(),
    /** `http(s)` only, and the database checks the same thing — a rider is a link somebody opens. */
    url: urlInputSchema(2000).nullish(),
    /** Et vedlegg som alt er lastet opp gjennom `POST /v1/attachments`. */
    attachmentId: z.uuid().nullish(),
    /**
     * Opprettet fra Avtale-fanen (#33). Avtaledokumentet havner da i `agreementDocuments` og ikke
     * i Stamdata, og de to definer-portene (0281) nekter å sende det til arrangøren.
     *
     * DEN KAN IKKE ENDRES ETTERPÅ — se `updateBookingArtistLinkRequestSchema`. Et dokument som
     * kunne flyttes ut av avtalen med en PATCH ville gjort «aldri delt» til «delt hvis noen
     * trykker feil».
     */
    isAgreement: z.boolean().optional(),
  })
  .refine((value) => value.url != null || value.attachmentId != null, {
    path: ['url'],
    message: 'An entry needs a link, a file, or both.',
  })
  .meta({ id: 'CreateBookingArtistLinkRequest' });

/**
 * Å RETTE EN LENKE. Atle, 9. september: «Mye av detaljene man legger til kan ikke redigeres.»
 *
 * En rider som får ny URL er den samme rideren — å slette og legge inn på nytt ville mistet
 * rekkefølgen og revisjonssporet, og tvunget den som retter en skrivefeil til å skrive alt om.
 */
export const updateBookingArtistLinkRequestSchema = z
  .object({
    kind: z.string().trim().min(1).max(60).optional(),
    label: shortTextSchema.nullish(),
    url: urlInputSchema(2000).nullish(),
    attachmentId: z.uuid().nullish(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: 'At least one field must be given.',
  })
  .meta({ id: 'UpdateBookingArtistLinkRequest' });

export type UpdateBookingArtistLinkRequest = z.infer<typeof updateBookingArtistLinkRequestSchema>;
export type BookingArtistMemberDto = z.infer<typeof bookingArtistMemberDtoSchema>;
export type BookingArtistLinkDto = z.infer<typeof bookingArtistLinkDtoSchema>;
export type BookingArtistRosterDto = z.infer<typeof bookingArtistRosterDtoSchema>;
export type CreateBookingArtistMemberRequest = z.infer<
  typeof createBookingArtistMemberRequestSchema
>;
export type UpdateBookingArtistMemberRequest = z.infer<
  typeof updateBookingArtistMemberRequestSchema
>;
export type CreateBookingArtistLinkRequest = z.infer<typeof createBookingArtistLinkRequestSchema>;

/**
 * ÉN GIG — festivalens programpost, lest av byrået (migrering 0096).
 *
 * ---------------------------------------------------------------------------------------------
 * Atle, 8. september: «alle gigs» på artistsiden. En gig blir til hos FESTIVALEN, ikke hos byrået:
 * det er en programpost i en årgang, med scene, klokkeslett og en status som beveger seg fra
 * muntlig bekreftet til signert til spilt. Byrået leser den gjennom en smal port og eier
 * ingenting av den — en kopi ville vært utdatert i det øyeblikket festivalen flyttet konserten.
 *
 * INGEN PENGER HER, og det er et valg: honoraret byrået avtalte står i byråets egen henvendelse.
 * Hva posten koster festivalen totalt er festivalens tall (vedtak 94.1), og et felt til her ville
 * vært en lekkasje ingen har bedt om.
 *
 * `startsAt` KAN VÆRE NULL. En programpost får klokkeslett når kjøreplanen legges, og en booking
 * som er bekreftet i mars har ofte bare en årgang og en scene. Årstallet er derfor med for seg
 * selv: det er alltid der, og det er det man sorterer en artistkarriere på.
 */
export const agencyGigDtoSchema = z
  .object({
    performanceId: z.uuid(),
    /** Huset som booket — organisasjonen, ikke festivalen: det er den byrået har avtale med. */
    organizerName: z.string(),
    festivalName: z.string(),
    editionName: z.string(),
    editionYear: z.number().int(),
    stageName: z.string().nullable(),
    startsAt: z.iso.datetime({ offset: true }).nullable(),
    endsAt: z.iso.datetime({ offset: true }).nullable(),
    status: performanceStatusSchema,
    /**
     * FINNES DEN DELTE ADVANCEN (QA runde 1 av spillesteder, P2-1). En gig er en programpost hos
     * arrangøren, og for et konsertbud finnes den før noen har laget advancen — «Delt» i
     * ADVANCE-kolonnen skal vente på advancen, ikke på programposten.
     */
    advanceShared: z.boolean(),
    /**
     * Den delte advancens id, når den finnes (ADVANCE-TILGANG §6): kolonnen «Tilgang» lenker til
     * `/artists/:artistId/advance/:advanceId?access=1`.
     */
    sharedAdvanceId: z.uuid().nullable().default(null),
    /** Engasjementet raden hører til (0927, grep F1): den ene statusen alle flatene leser. */
    engagement: engagementRefDtoSchema.nullable().optional(),
  })
  .meta({ id: 'AgencyGig' });

/**
 * HVA BYRÅET SELV TJENER PÅ ARTISTEN I ÅR (vedtak 102.1, migrering 0165).
 *
 * ================================================================================================
 * Atle, 11. september: «Prosent per artist med en standardsats for huset, regnet av honoraret
 * alene. Satsen kan overstyres per artist.»
 *
 * Vedtak 85 bygde den andre halvdelen 9. september: når en booking signeres, føres honoraret som en
 * KOSTNAD hos arrangøren. Dette er byråets side av det samme beløpet.
 *
 * ================================================================================================
 * REGNET, IKKE LAGRET — av samme grunn som `totalMinor` på artisten.
 *
 * Satsen kan endres, og en lagret provisjon ville vært tallet som blir feil den dagen noen retter
 * den. Beløpet er en funksjon av honorarene og satsen, og begge finnes.
 *
 * `rateBp` ER DEN SATSEN SOM FAKTISK GJELDER — artistens når den er satt, ellers husets — og
 * `fromArtist` sier hvilken av de to det ble. Skjermen trenger begge for å kunne si «15 % (husets)»
 * framfor bare et tall.
 *
 * NULL SATS SKJULER HELE TALLET. Et byrå som ikke har sagt hva de tar skal ikke få en årssum
 * regnet av en sats produktet fant på.
 */
/* Flyttet til `artist-agreement.ts` (292.1): den delte Avtale-fanen bruker den samme formen. */
export { artistCommissionSchema, type ArtistCommission } from './artist-agreement.js';

/** Ikke paget: en artist har titalls gigs, ikke tusener, og siden viser dem alle. */
export const agencyGigsDtoSchema = z
  .object({ items: z.array(agencyGigDtoSchema) })
  .meta({ id: 'AgencyGigs' });

export type AgencyGigDto = z.infer<typeof agencyGigDtoSchema>;
export type AgencyGigsDto = z.infer<typeof agencyGigsDtoSchema>;

/* ------------------------------------------------------ reisefølget per booking (0378, sak 216) */

const oneBooking = (value: {
  agencyBookingId?: string | undefined;
  enquiryId?: string | undefined;
}) => (value.agencyBookingId === undefined) !== (value.enquiryId === undefined);

/** Nøyaktig én av de to bookingformene: byråets egen, eller en forespørsel fra en festival. */
export const bookingPartyQuerySchema = z
  .object({ agencyBookingId: z.uuid().optional(), enquiryId: z.uuid().optional() })
  .refine(oneBooking, { message: 'Name exactly one booking.', path: ['agencyBookingId'] })
  .meta({ id: 'BookingPartyQuery' });
export type BookingPartyQuery = z.infer<typeof bookingPartyQuerySchema>;

export const bookingPartyMemberSchema = z
  .object({
    memberId: z.uuid(),
    displayName: z.string(),
    role: z.string().nullable(),
    inParty: z.boolean(),
    /** Innloggingen byrået har til mennesket, samme verdi som på medlemmet. */
    login: z.enum(['none', 'invited', 'requested', 'active']),
  })
  .meta({ id: 'BookingPartyMember' });
export type BookingPartyMember = z.infer<typeof bookingPartyMemberSchema>;

export const bookingPartySchema = z
  .object({
    members: z.array(bookingPartyMemberSchema),
    /**
     * ADVANCEN ER DELT, og da er det advancens Personer-fane som er reisefølget (216.16). Lista her
     * står, men skjermen sier hvor den som gjelder bor.
     */
    sharedAdvance: z.boolean(),
    /**
     * ARTISTENS MANAGEMENT HOS BYRÅET (260.3, 260.4). Følger med når byrået trykker «Del
     * reisefølget», og knappen sier hvor mange som deles. Valgfri, så en eldre klient tåler svaret.
     */
    management: z
      .array(
        z.object({ contactId: z.uuid(), displayName: z.string(), role: z.string().nullable() }),
      )
      .optional(),
  })
  .meta({ id: 'BookingParty' });
export type BookingParty = z.infer<typeof bookingPartySchema>;

export const setBookingPartyRequestSchema = z
  .object({
    agencyBookingId: z.uuid().optional(),
    enquiryId: z.uuid().optional(),
    memberIds: z.array(z.uuid()).max(100),
  })
  .refine(oneBooking, { message: 'Name exactly one booking.', path: ['agencyBookingId'] })
  .meta({ id: 'SetBookingPartyRequest' });
export type SetBookingPartyRequest = z.infer<typeof setBookingPartyRequestSchema>;
